import React from 'react';
import { Calendar } from 'lucide-react';

import { PageHeader } from '@/components/ui/page-header';

/**
 * Le bandeau d'accueil — la première chose qu'un exploitant voit le matin.
 *
 * Son rôle n'est pas de mesurer: les chiffres commencent juste en dessous. Il
 * situe la personne et la journée, et c'est délibéré. Ce produit s'adresse à
 * des gens que l'informatique met mal à l'aise; ouvrir sur une grille de
 * compteurs les met devant un tableau de bord d'avion, ouvrir sur leur nom et
 * la date du jour les met devant un bureau.
 *
 * L'illustration est dessinée ici, en SVG, plutôt que chargée comme image:
 * elle doit suivre le thème clair comme sombre, et un PNG ne le fait pas.
 */

const SALUTATIONS = [
  // Le découpage suit l'usage courant au Mali, pas l'horloge astronomique:
  // « bonsoir » commence tôt, vers seize heures.
  { avant: 5, mot: 'BONNE NUIT' },
  { avant: 12, mot: 'BONJOUR' },
  { avant: 16, mot: 'BON APRÈS-MIDI' },
  { avant: 24, mot: 'BONSOIR' },
];

export const salutationPour = (date = new Date()) =>
  SALUTATIONS.find((creneau) => date.getHours() < creneau.avant)?.mot ||
  'BONJOUR';

/** Le prénom seul: « HAMIDOU DIT TIEMOKHOBA KEITA » ne tient pas dans une ligne. */
export const prenomDe = (nomComplet) => {
  const propre = String(nomComplet || '').trim();
  if (!propre) return '';
  return propre.split(/\s+/)[0];
};

/**
 * « Jeudi 15 octobre 2026 »: majuscule au seul premier mot. La classe
 * `capitalize` donnait « Jeudi 15 Octobre 2026 », une graphie anglaise.
 */
export const dateLongue = (date) => {
  const texte = date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return texte.charAt(0).toUpperCase() + texte.slice(1);
};

/** Un mot à trait d'union (E-CARBU) ne se coupe jamais en fin de ligne. */
export const titreInsecable = (titre) => {
  if (typeof titre !== 'string' || !titre.includes('-')) return titre;
  return titre.split(/(\s+)/).map((morceau, index) =>
    morceau.includes('-') ? (
      // eslint-disable-next-line react/no-array-index-key
      <span key={index} className="whitespace-nowrap">
        {morceau}
      </span>
    ) : (
      morceau
    ),
  );
};

export const WelcomeHero = ({
  nomUtilisateur,
  titre = 'Bienvenue sur E-CARBU Transport',
  description = 'Suivez votre flotte, gérez vos approvisionnements et gardez le contrôle, en toute simplicité.',
  date = new Date(),
  actions,
  meta,
  stats,
  statsPlus,
  statsDisposition = 'grille',
  alertes,
  /** Icône « À traiter » posée à côté de la date (voir `AlerteATraiter`). */
  aTraiter,
  compact = false,
  className,
  ...rest
}) => {
  const prenom = prenomDe(nomUtilisateur);
  return (
    <div data-testid="dashboard-welcome" {...rest}>
      <PageHeader
        className={className}
        eyebrow={`${salutationPour(date)}${prenom ? `, ${prenom}` : ''}`}
        title={titreInsecable(titre)}
        description={description}
        illustration="flotte"
        stats={stats}
        statsPlus={statsPlus}
        alertes={alertes}
        actions={<>{actions}{aTraiter}</>}
        meta={<>{meta}<span className="repere-date"><Calendar className="h-3.5 w-3.5" aria-hidden="true" />{dateLongue(date)}</span></>}
      />
    </div>
  );
};

export default WelcomeHero;
